<template>
	<div>
		<b-alert v-if="showSuccess" :show="true" variant="success">
			<div
				v-if="formConfig.thankYouMessage"
				v-html="formConfig.thankYouMessage"
			></div>
			<div v-else>{{ $t("success") }}</div>
		</b-alert>
		<b-overlay v-else :show="saving" class="d-flex flex-column">
			<b-alert :show="!!errorMsg" variant="danger">{{ errorMsg }}</b-alert>

			<b-row
				v-for="formModule in formConfig.fieldsConfiguration"
				:key="formModule.name"
				:class="[
					{ 'order-first': formModule.name == 'MembershipDetails' },
					'confirmation-' + formModule.name,
				]"
				class="mt-4"
			>
				<b-col>
					<div v-html="formModule.description" />

					<template v-if="!formModule.isCustom">
						<b-row>
							<b-col cols="12" md="6">
								<base-text-row
									v-for="field in formModule.fields
										.filter((x) => x.isVisible && !isHiddenFromConfirmationForm(x))
										.filter((x, i, a) => i < Math.ceil(a.length / 2))"
									:key="field.key"
									:label="getLabel(field)"
									:value="getFieldValue(field)"
								/>
							</b-col>
							<b-col cols="12" md="6">
								<base-text-row
									v-for="field in formModule.fields
										.filter((x) => x.isVisible && !isHiddenFromConfirmationForm(x))
										.filter((x, i, a) => i >= Math.ceil(a.length / 2))"
									:key="field.key"
									:label="getLabel(field)"
									:value="getFieldValue(field)"
								/>
								<!-- FOYS-26217: the State is added by AddressModule, not by the form configuration. -->
								<base-text-row
									v-if="isAddressModule(formModule) && selectedStateName"
									:label="$t('state')"
									:value="selectedStateName"
								/>
							</b-col>
						</b-row>
					</template>

					<div v-if="formModule.isCustom && formModule.name === 'ParentInfo'">
						<b-table
							class="mt-2"
							:fields="parentInfoModuleFields"
							:items="formInput.ParentInfos"
							borderless
						/>
					</div>

					<div v-if="formModule.isCustom && formModule.name === 'Files'">
						<p v-for="file in formInput.Files" :key="file.fileName">
							{{ file.fileName }}
						</p>
					</div>
				</b-col>
			</b-row>

			<b-row
				v-if="formConfig.allowDirectPayment && totalAmountToPay > 0"
				class="mt-4 confirmation-Price"
			>
				<b-col cols="12" md="6">
					<base-text-row
						v-for="(cost, index) in planAdditionalCosts"
						:key="`plan-additional-cost-${index}`"
						:label="cost.description || $t('additional_cost')"
						:value="`€ ${formatAmount(cost.amount)}`"
					/>
					<base-text-row
						v-if="voucherDiscount"
						:label="$t('discount')"
						:value="
							`€ ${voucherDiscount.toLocaleString(undefined, {
								maximumFractionDigits: 2,
								minimumFractionDigits: 2,
							})}`
						"
					/>
					<base-text-row
						class="total-price-container font-weight-bold"
						:label="$t('total_to_pay')"
						:value="`€ ${totalPrice}`"
						:label-class="'font-weight-normal'"
					/>
				</b-col>
			</b-row>

			<validation-observer v-slot="{ handleSubmit }">
				<b-row v-if="isAgreementEnabled" class="mt-4">
					<b-col>
						<p>
							<validation-provider
								v-slot="{ errors }"
								:rules="{ required: true }"
								:name="$t('conditions_checkbox')"
							>
								<b-form-checkbox v-model="agreement" validatable>
									{{ $t("agree_with_the") }}
									<a
										v-if="formConfig.termsAndConditionsUrl"
										target="_blank"
										:href="formConfig.termsAndConditionsUrl"
										>{{ $t("terms_and_conditions") }}</a
									>
									<template v-else-if="formConfig.termsAndConditionsText">
										{{ $t("terms_and_conditions") }}
									</template>
									<template
										v-if="
											(formConfig.termsAndConditionsUrl ||
												formConfig.termsAndConditionsText) &&
												formConfig.privacyPolicyUrl
										"
									>
										{{ $t("agree_and") }}
									</template>
									<a
										v-if="formConfig.privacyPolicyUrl"
										target="_blank"
										:href="formConfig.privacyPolicyUrl"
									>
										{{ $t("privacy_policy") }}
									</a>
								</b-form-checkbox>
								<p v-for="error in errors" :key="error" class="text-danger mb-0">
									{{ error }}
								</p>
							</validation-provider>
						</p>

						<p
							v-if="formConfig.termsAndConditionsText"
							v-html="formConfig.termsAndConditionsText"
						/>
					</b-col>
				</b-row>

				<b-row v-if="isRecaptchaEnabled" class="mt-4">
					<b-col class="d-flex justify-content-center">
						<base-recaptcha
							:site-key="formConfig.recaptchaSiteKey"
							@verify="onRecaptchaVerify"
						/>
					</b-col>
				</b-row>

				<b-row>
					<b-col class="text-center mb-5">
						<hr />
						<b-btn
							variant="secondary"
							:disabled="saving"
							@click.stop.prevent="$emit('cancel')"
						>
							{{ $t("go_back") }}
						</b-btn>
						<b-btn
							:disabled="saving || (isRecaptchaEnabled && !recaptchaToken)"
							class="mr-2 ml-2 confirm-button"
							variant="primary"
							@click.stop.prevent="handleSubmit(submit)"
						>
							{{
								formConfig.allowDirectPayment && totalAmountToPay > 0
									? $t("confirm_and_pay")
									: $t("confirm")
							}}
						</b-btn>
					</b-col>
				</b-row>
			</validation-observer>

			<template v-if="showPendingLinkModal" #overlay>
				<div class="text-center p-4 bg-white rounded shadow">
					<h5>{{ $t("pending_link_title") }}</h5>
					<p>{{
						$t("pending_link_confirmation", { email: pendingLinkMaskedEmail })
					}}</p>
					<b-btn variant="primary" @click="onPendingLinkModalHidden">
						{{ $t("ok") }}
					</b-btn>
				</div>
			</template>
		</b-overlay>
	</div>
</template>

<script>
import axios from "axios";
import _ from "lodash";
import { appSettings } from "@/appSettings";
import { shortDateFilter, planMixin, normalizeRedirectUrl } from "@/shared";
import { countryHelper } from "../countryHelper.js";
import { stateHelper } from "../stateHelper.js";

export default {
	mixins: [countryHelper, stateHelper, planMixin],
	props: {
		formInput: null,
		formConfig: null,
		voucherData: null,
	},
	data() {
		return {
			errorMsg: null,
			saving: false,
			showSuccess: false,
			pendingLinkMaskedEmail: null,
			showPendingLinkModal: false,
			agreement: false,
			recaptchaToken: null,
		};
	},
	computed: {
		selectedStateName() {
			const address = this.formInput && this.formInput.AddressInfo;
			return address ? this.getStateNameByCode(address.stateCode) : null;
		},
		isAgreementEnabled() {
			return !!(
				this.formConfig.termsAndConditionsText ||
				this.formConfig.termsAndConditionsUrl ||
				this.formConfig.privacyPolicyUrl
			);
		},
		isRecaptchaEnabled() {
			return !!(
				this.formConfig.recaptchaSiteKey &&
				this.formConfig.recaptchaSiteKey.length > 0
			);
		},
		registrationFormUrl() {
			return `${appSettings.FoysPubRegistrationsUrl}/registration-forms/${this.formConfig.id}`;
		},
		parentInfoModuleFields() {
			return [
				{ label: this.$t("name"), key: "parentName" },
				{
					label: this.$t("relationship"),
					key: "relationship",
				},
				{ label: this.$t("email"), key: "email" },
				{ label: this.$t("phone"), key: "phone" },
				{
					label: this.$t("wants_to_receive_emails"),
					key: "receiveEmails",
				},
			];
		},
		servicesField() {
			var servicesModule = this.formConfig.fieldsConfiguration.find(
				(x) => x.name === "Services"
			);

			if (!servicesModule) return null;

			return servicesModule.fields.find((x) => x.key == "ServiceIds");
		},
		selectedPlan() {
			return this.formInput.selectedPlan;
		},
		// FOYS-22183: the plan price includes these costs; they are shown as separate lines
		// above the total, so the plan line is shown without them.
		planAdditionalCosts() {
			if (
				!this.selectedPlan ||
				!this.selectedPlan.additionalCosts ||
				!this.formConfig.allowDirectPayment ||
				this.totalAmountToPay <= 0
			) {
				return [];
			}

			return this.selectedPlan.additionalCosts.filter((x) => x.amount > 0);
		},
		selectedPlanWithoutAdditionalCosts() {
			if (!this.selectedPlan || this.planAdditionalCosts.length === 0) {
				return this.selectedPlan;
			}

			var additionalCostsTotal = this.planAdditionalCosts.reduce(
				(sum, x) => sum + x.amount,
				0
			);

			return {
				...this.selectedPlan,
				price: this.selectedPlan.price - additionalCostsTotal,
			};
		},
		voucherDiscount() {
			if (!this.voucherData) return 0;

			if (this.voucherData.amountType === "Amount") {
				return this.voucherData.amount;
			} else if (this.voucherData.amountType === "Percent") {
				var discount =
					this.totalPriceWithoutDiscount * (this.voucherData.amount / 100);
				return discount;
			}

			return 0;
		},
		// Net amount the registrant actually has to pay, after voucher discount
		// and floored at 0. Used both for the displayed total and to decide the
		// confirm-button label: a €0 registration reads "Confirm", not
		// "Confirm and pay" (FOYS-24699).
		totalAmountToPay() {
			var total = this.totalPriceWithoutDiscount;

			if (this.voucherDiscount) {
				total = total - this.voucherDiscount;
			}

			if (total < 0) total = 0;

			return total;
		},
		totalPrice() {
			return this.totalAmountToPay.toLocaleString(undefined, {
				maximumFractionDigits: 2,
				minimumFractionDigits: 2,
			});
		},
		totalPriceWithoutDiscount() {
			var totalPriceWithoutDiscount = 0;

			if (this.selectedPlan) {
				totalPriceWithoutDiscount += this.selectedPlan.price;
				if (this.selectedPlan.registrationFee) {
					totalPriceWithoutDiscount += this.selectedPlan.registrationFee;
				}
				if (this.selectedPlan.remittanceFee) {
					totalPriceWithoutDiscount += this.selectedPlan.remittanceFee;
				}
				if (this.selectedPlan.transferFee) {
					totalPriceWithoutDiscount += this.selectedPlan.transferFee;
				}
				if (this.selectedPlan.discountFee) {
					totalPriceWithoutDiscount += this.selectedPlan.discountFee;
				}
				if (this.selectedPlan.administrationFee) {
					totalPriceWithoutDiscount += this.selectedPlan.administrationFee;
				}
			}

			if (
				this.servicesField &&
				this.formInput.ServiceIds &&
				this.formInput.ServiceIds.length > 0
			) {
				this.formInput.ServiceIds.forEach((serviceId) => {
					var service = this.servicesField.options.find((el) => el.id === serviceId);

					if (service.price) {
						totalPriceWithoutDiscount += service.price;
					}
				});
			}

			return totalPriceWithoutDiscount;
		},
	},
	mounted() {
		window.scrollTo({ top: 0, behavior: "smooth" });
	},
	methods: {
		isHiddenFromConfirmationForm(field) {
			var fieldsHiddenInConfirmationForm = [
				"Password",
				"ConfirmPassword",
				"ConfirmEmail",
			];
			return fieldsHiddenInConfirmationForm.includes(field.key);
		},
		formatAmount(amount) {
			return amount.toLocaleString(undefined, {
				maximumFractionDigits: 2,
				minimumFractionDigits: 2,
			});
		},
		isAddressModule(formModule) {
			return (formModule.fields || []).some((x) => x.key && x.key.startsWith("AddressInfo."));
		},
		getLabel(field) {
			return field.customLabel ? field.customLabel : field.label;
		},
		getFieldValue(field) {
			var defaultValue = "-";
			var val = _.get(this.formInput, field.key);

			if (val === null || val === undefined) {
				return defaultValue;
			}

			if (field.customFieldId) {
				val = val.value;
			}

			switch (field.type) {
				case "String":
				case "Number":
				case "Email":
					return val;
				case "Date":
					return shortDateFilter(val);
				case "Boolean":
					return this.$t(val ? "yes" : "no");
				case "Plan":
					return this.getPlanDisplayLabel(
						this.selectedPlanWithoutAdditionalCosts,
						this.formConfig.hidePrices
					);
				case "Dropdown":
				case "ContributionTerms":
				case "Organisation":
					var selectedOption = field.options.find((el) =>
						field.customFieldId ? el.code === val : el.id === val
					);

					return selectedOption ? selectedOption.name : defaultValue;
				case "Discipline":
				case "MultiSelect":
					if (field.customFieldId) {
						var selectedOptionCodes = val.split(", ");
						return field.options
							.filter((el) => selectedOptionCodes.includes(el.code))
							.map((el) => el.name)
							.join(", ");
					}

					// Discipline options hold one row per club, all with the same id.
					return _.uniqBy(
						field.options.filter((el) => val && val.includes(el.id)),
						"id"
					)
						.map((el) => el.name)
						.join(", ");

				case "Newsletters":
				case "MarketingInterests": {
					let selectedOptions = field.options
						.filter((el) => val && val.includes(el.code))
						.map((el) => el.name)
						.join(", ");

					return selectedOptions;
				}
				case "Country":
					return val ? this.getCountryNameById(val) : defaultValue;
				case "Image":
					return val.fileName ?? defaultValue;
				case "Language": {
					let selectedOption = field.options.find(
						(el) => el.cultureInfoName === val
					);
					return selectedOption ? selectedOption.displayName : defaultValue;
				}
				case "Services": {
					let selectedOptions = field.options
						.filter((el) => val && val.includes(el.id))
						.map((el) => el.name)
						.join(", ");

					return selectedOptions;
				}
			}
		},
		onRecaptchaVerify(token) {
			this.recaptchaToken = token;
		},
		prepareInput() {
			var input = { ...this.formInput };
			if (input.CustomFieldValues) {
				input.CustomFieldValues = input.CustomFieldValues.filter((x) => x);
			}

			if (input.ConfirmPassword) {
				delete input.ConfirmPassword;
			}

			if (input.ConfirmEmail) {
				delete input.ConfirmEmail;
			}

			return input;
		},
		showAndWaitPendingLinkModal() {
			this.showPendingLinkModal = true;
			return new Promise((resolve) => {
				this._pendingLinkResolve = resolve;
			});
		},
		onPendingLinkModalHidden() {
			if (this._pendingLinkResolve) {
				this._pendingLinkResolve();
				this._pendingLinkResolve = null;
			}
		},
		submit() {
			this.errorMsg = null;
			this.saving = true;

			var requestData = this.prepareInput();
			if (this.recaptchaToken) {
				requestData.recaptchaToken = this.recaptchaToken;
			}

			axios
				.post(this.registrationFormUrl, requestData)
				.then(async (response) => {
					if (response.data.pendingLinkMaskedEmail) {
						this.pendingLinkMaskedEmail = response.data.pendingLinkMaskedEmail;
						await this.showAndWaitPendingLinkModal();
					}

					const successRedirectUrl = normalizeRedirectUrl(this.formConfig.successRedirectUrl);
					// For a zero-amount invoice the backend returns the success redirect URL as the payment link.
					const paymentLink = normalizeRedirectUrl(response.data.paymentLink);

					if (this.formConfig.allowDirectPayment && paymentLink) {
						window.location.href = paymentLink;
					} else if (window.flutter_inappwebview) {
						// This is to continue in flutter app after registration
						window.flutter_inappwebview.callHandler("registrationForm", {
							username: response.data.username,
							password: this.formInput.Password,
						});
					} else if (successRedirectUrl) {
						window.location.href = successRedirectUrl;
					} else {
						this.showSuccess = true;

						window.dispatchEvent(
							new CustomEvent("login", {
								detail: {
									username: response.data.username,
									password: this.formInput.Password,
								},
							})
						);
					}
				})
				.catch((error) => {
					if (error.response && error.response.data && error.response.data.detail) {
						this.errorMsg = error.response.data.detail;
					} else {
						this.errorMsg = this.$t("something_went_wrong");
					}
				})
				.then(() => {
					this.saving = false;
				});
		},
	},
};
</script>
